import React, { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreatePropertyMutation, useUpdatePropertyMutation } from "@/store/rest_api";
import { toast } from "sonner";
import Property from "@/model/interface/Property";
import { Loader2 } from "lucide-react";

const PROPERTY_TYPES = [
  { value: "SINGLE_FAMILY", label: "Single Family" },
  { value: "MULTI_FAMILY", label: "Multi Family" },
  { value: "CONDO", label: "Condo" },
  { value: "TOWNHOUSE", label: "Townhouse" },
  { value: "MOBILE_HOME", label: "Mobile Home" },
  { value: "LAND", label: "Land" },
  { value: "COMMERCIAL", label: "Commercial" },
  { value: "INDUSTRIAL", label: "Industrial" },
  { value: "MIXED_USE", label: "Mixed Use" },
  { value: "OTHER", label: "Other" },
];

const OWNER_TYPES = [
  { value: "OWNER_OCCUPIED", label: "Owner Occupied" },
  { value: "ABSENTEE", label: "Absentee" },
  { value: "CORPORATE", label: "Corporate" },
  { value: "TRUST", label: "Trust" },
  { value: "UNKNOWN", label: "Unknown" },
];

const propertySchema = z.object({
  address: z.string().min(1, "Address is required"),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
  county: z.string().optional(),
  propertyType: z.string().optional(),
  beds: z.coerce.number().int().positive().optional().or(z.literal("")),
  baths: z.coerce.number().positive().optional().or(z.literal("")),
  sqft: z.coerce.number().int().positive().optional().or(z.literal("")),
  lotSizeSqft: z.coerce.number().int().positive().optional().or(z.literal("")),
  yearBuilt: z.coerce.number().int().min(1600).max(2100).optional().or(z.literal("")),
  taxAssessedValue: z.coerce.number().nonnegative().optional().or(z.literal("")),
  marketValue: z.coerce.number().nonnegative().optional().or(z.literal("")),
  equityEstimate: z.coerce.number().optional().or(z.literal("")),
  mortgageBalance: z.coerce.number().nonnegative().optional().or(z.literal("")),
  ownerName: z.string().optional(),
  ownerType: z.string().optional(),
  zoning: z.string().optional(),
});

type PropertyFormValues = z.infer<typeof propertySchema>;

interface PropertyFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  property?: Property;
}

export function PropertyFormDialog({ open, onOpenChange, property }: PropertyFormDialogProps) {
  const isEditing = !!property;
  const [createProperty, { isLoading: creating }] = useCreatePropertyMutation();
  const [updateProperty, { isLoading: updating }] = useUpdatePropertyMutation();

  const form = useForm<PropertyFormValues>({
    resolver: zodResolver(propertySchema),
    defaultValues: {
      address: "",
      city: "",
      state: "",
      zip: "",
      county: "",
      propertyType: "",
      beds: "",
      baths: "",
      sqft: "",
      lotSizeSqft: "",
      yearBuilt: "",
      taxAssessedValue: "",
      marketValue: "",
      equityEstimate: "",
      mortgageBalance: "",
      ownerName: "",
      ownerType: "",
      zoning: "",
    },
  });

  useEffect(() => {
    if (open) {
      if (property) {
        form.reset({
          address: property.address || "",
          city: property.city || "",
          state: property.state || "",
          zip: property.zip || "",
          county: property.county || "",
          propertyType: property.propertyType || "",
          beds: property.beds ?? "",
          baths: property.baths ?? "",
          sqft: property.sqft ?? "",
          lotSizeSqft: property.lotSizeSqft ?? "",
          yearBuilt: property.yearBuilt ?? "",
          taxAssessedValue: property.taxAssessedValue ?? "",
          marketValue: property.marketValue ?? "",
          equityEstimate: property.equityEstimate ?? "",
          mortgageBalance: property.mortgageBalance ?? "",
          ownerName: property.ownerName || "",
          ownerType: property.ownerType || "",
          zoning: property.zoning || "",
        });
      } else {
        form.reset();
      }
    }
  }, [property, open]);

  const onSubmit = async (values: PropertyFormValues) => {
    const payload: any = {
      address: values.address,
      city: values.city || null,
      state: values.state || null,
      zip: values.zip || null,
      county: values.county || null,
      propertyType: values.propertyType || null,
      beds: values.beds || null,
      baths: values.baths || null,
      sqft: values.sqft || null,
      lotSizeSqft: values.lotSizeSqft || null,
      yearBuilt: values.yearBuilt || null,
      taxAssessedValue: values.taxAssessedValue || null,
      marketValue: values.marketValue || null,
      equityEstimate: values.equityEstimate || null,
      mortgageBalance: values.mortgageBalance || null,
      ownerName: values.ownerName || null,
      ownerType: values.ownerType || null,
      zoning: values.zoning || null,
    };

    try {
      if (isEditing) {
        await updateProperty({ id: property.id, data: payload }).unwrap();
        toast.success("Property updated");
      } else {
        await createProperty(payload).unwrap();
        toast.success("Property created");
      }
      onOpenChange(false);
    } catch {
      toast.error(isEditing ? "Failed to update property" : "Failed to create property");
    }
  };

  const isSubmitting = creating || updating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Property" : "Add Property"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField control={form.control} name="address" render={({ field }) => (
              <FormItem>
                <FormLabel>Address *</FormLabel>
                <FormControl><Input placeholder="123 Main St" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <div className="grid grid-cols-3 gap-4">
              <FormField control={form.control} name="city" render={({ field }) => (
                <FormItem>
                  <FormLabel>City</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                </FormItem>
              )} />
              <FormField control={form.control} name="state" render={({ field }) => (
                <FormItem>
                  <FormLabel>State</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                </FormItem>
              )} />
              <FormField control={form.control} name="zip" render={({ field }) => (
                <FormItem>
                  <FormLabel>ZIP</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                </FormItem>
              )} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="county" render={({ field }) => (
                <FormItem>
                  <FormLabel>County</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                </FormItem>
              )} />
              <FormField control={form.control} name="propertyType" render={({ field }) => (
                <FormItem>
                  <FormLabel>Property Type</FormLabel>
                  <Select value={field.value?.toString()} onValueChange={field.onChange}>
                    <FormControl><SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger></FormControl>
                    <SelectContent>
                      {PROPERTY_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )} />
            </div>

            <div className="grid grid-cols-4 gap-4">
              <FormField control={form.control} name="beds" render={({ field }) => (
                <FormItem>
                  <FormLabel>Beds</FormLabel>
                  <FormControl><Input type="number" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="baths" render={({ field }) => (
                <FormItem>
                  <FormLabel>Baths</FormLabel>
                  <FormControl><Input type="number" step="0.5" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="sqft" render={({ field }) => (
                <FormItem>
                  <FormLabel>Sqft</FormLabel>
                  <FormControl><Input type="number" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="yearBuilt" render={({ field }) => (
                <FormItem>
                  <FormLabel>Year Built</FormLabel>
                  <FormControl><Input type="number" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            <FormField control={form.control} name="lotSizeSqft" render={({ field }) => (
              <FormItem>
                <FormLabel>Lot Size (sqft)</FormLabel>
                <FormControl><Input type="number" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="marketValue" render={({ field }) => (
                <FormItem>
                  <FormLabel>Market Value</FormLabel>
                  <FormControl><Input type="number" {...field} /></FormControl>
                </FormItem>
              )} />
              <FormField control={form.control} name="taxAssessedValue" render={({ field }) => (
                <FormItem>
                  <FormLabel>Tax Assessed Value</FormLabel>
                  <FormControl><Input type="number" {...field} /></FormControl>
                </FormItem>
              )} />
              <FormField control={form.control} name="equityEstimate" render={({ field }) => (
                <FormItem>
                  <FormLabel>Equity Estimate</FormLabel>
                  <FormControl><Input type="number" {...field} /></FormControl>
                </FormItem>
              )} />
              <FormField control={form.control} name="mortgageBalance" render={({ field }) => (
                <FormItem>
                  <FormLabel>Mortgage Balance</FormLabel>
                  <FormControl><Input type="number" {...field} /></FormControl>
                </FormItem>
              )} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="ownerName" render={({ field }) => (
                <FormItem>
                  <FormLabel>Owner Name</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                </FormItem>
              )} />
              <FormField control={form.control} name="ownerType" render={({ field }) => (
                <FormItem>
                  <FormLabel>Owner Type</FormLabel>
                  <Select value={field.value?.toString()} onValueChange={field.onChange}>
                    <FormControl><SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger></FormControl>
                    <SelectContent>
                      {OWNER_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )} />
            </div>

            <FormField control={form.control} name="zoning" render={({ field }) => (
              <FormItem>
                <FormLabel>Zoning</FormLabel>
                <FormControl><Input {...field} /></FormControl>
              </FormItem>
            )} />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="animate-spin mr-1 h-4 w-4" />}
                {isEditing ? "Update Property" : "Add Property"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
